import React, { useRef, useEffect, useState } from "react";
import { CssVarsProvider } from "@mui/joy/styles";
import CssBaseline from "@mui/joy/CssBaseline";
import Box from "@mui/joy/Box";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import Breadcrumbs from "../components/Breadcrumbs";
import { useSidebarItems } from "../components/SidebarItems";

const AppContent = () => {
  const sidebarItems = useSidebarItems();
  const location = useLocation();
  const [refreshKey, setRefreshKey] = useState<string>(
    `${location.pathname}-0`
  );
  const previousPathnameRef = useRef(location.pathname);
  const refreshCountRef = useRef(0);

  // Update refresh key when pathname or location state changes
  useEffect(() => {
    const locationRefresh = (location.state as any)?.refresh;
    // Always update refreshKey when there's a refresh timestamp in state (new navigation)
    // or when pathname changes
    if (locationRefresh) {
      setRefreshKey(`${location.pathname}-${locationRefresh}`);
      previousPathnameRef.current = location.pathname;
    } else if (previousPathnameRef.current !== location.pathname) {
      refreshCountRef.current += 1;
      setRefreshKey(`${location.pathname}-${refreshCountRef.current}`);
      previousPathnameRef.current = location.pathname;
    }
  }, [location.pathname, location.state]);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Header />
      <Sidebar />
      <Box
        component="main"
        className="MainContent"
        sx={{
          px: { xs: 2, md: 6 },
          pt: {
            xs: "calc(12px + var(--Header-height))",
            sm: "calc(12px + var(--Header-height))",
            md: 3,
          },
          pb: { xs: 2, sm: 2, md: 3 },
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          height: "100vh",
          overflow: "auto",
          scrollbarGutter: "stable",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Breadcrumbs />
        </Box>
        <Routes>
          {sidebarItems.map((item) => {
            return (
              <Route
                key={`${item.path}-${refreshKey}`}
                path={item.path}
                element={
                  React.isValidElement(item.element)
                    ? React.cloneElement(item.element, {
                        key: refreshKey,
                      } as any)
                    : item.element
                }
              />
            );
          })}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Box>
    </Box>
  );
};

export default function Dashboard() {
  return (
    <CssVarsProvider disableTransitionOnChange>
      <CssBaseline />
      <Router>
        <AppContent />
      </Router>
    </CssVarsProvider>
  );
}
